/**
 * Utils/fetchLocationData.js
 *
 * Public fetcher API for the LocationPage grid.
 *
 * Exports (all named) used by LocationGridLayout.js:
 *   - fetchLocationData(locationId, year)
 *   - fetchLocationIdOnly(locationId)
 *   - fetchCountryIdLocationId(countryId, locationId)
 *   - fetchLocationNameYear(locationName, year)
 *   - fetchUserIdLocationNameYear(userId, locationName, year)
 *   - fetchUserIdCountryIdLocationId(userId, countryId, locationId)
 *   - extractMediaByType(formattedData, contentType)
 *   - formatLocationData(raw)
 *   - default (convenience object with the above)
 */

import LocationStructure from "../Constants/CommonStructureOnLocation";
// ↑ adjust this path to match the actual constant file if it differs.

// ─── Base URL ────────────────────────────────────────────────────────────────
const BASE = "/api/mock/LocationPage";

// ─── Internal helpers ────────────────────────────────────────────────────────

async function fetchJSON(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `Failed to fetch ${url}: ${response.status} ${response.statusText}`
    );
  }
  return response.json();
}

function toArray(value) {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
}

function isFoodRecord(entry) {
  return (
    entry &&
    typeof entry === "object" &&
    "foodName" in entry &&
    "foodtype" in entry
  );
}

// ─── Media extractor ─────────────────────────────────────────────────────────

export function extractMediaByType(formattedData, contentType) {
  if (!formattedData?.feeds) return [];

  const typeMap = {
    images: "image",
    reels:  "reel",
    videos: "video",
  };
  const targetType = typeMap[contentType] || contentType;

  return formattedData.feeds.flatMap((feed) =>
    (feed.medias || [])
      .filter(
        (m) =>
          m.mediatype === targetType ||
          (typeof m.mediatype === "string" &&
            m.mediatype.startsWith(`${targetType}/`))
      )
      .map((media) => ({
        id:          media.mediaId,
        imageurl:    media.imageurl,
        description: media.descriptions,
        posted_by:   media.uploadedBy || feed.userId || "Anonymous",
        user_avatar: feed.userimage,
        timestamp:   feed.timestamp,
        location:    feed.locations?.[0]?.locationId || "",
        impressions: media.nlikes,
        mediatype:   media.mediatype,
      }))
  );
}

// ─── Data formatter ──────────────────────────────────────────────────────────

export function formatLocationData(raw) {
  if (!raw || typeof raw !== "object") return { ...LocationStructure };

  // Accept either the new shape directly, or the legacy event-shaped input.
  const r = raw;

  console.log("raw in formatLocationData: ", raw); 

  // ── Advertises ─────────────────────────────────────────────────────────────
  const advertises = (r.advertises || []).map((ad) => ({
    advertiseId:       ad.advertiseId       ?? "",
    advertiseUUID:     ad.advertiseUUID     ?? "",
    profileId:         ad.profileId         ?? "",
    advertiseImageUrl: ad.advertiseImageUrl ?? "",
    PlannedDate:       ad.PlannedDate       ?? "",
    postedBy:          ad.postedBy          ?? "",
    postedOn:          ad.postedOn          ?? "",
  }));

  // ── Users ──────────────────────────────────────────────────────────────────
  const users = (r.users || []).map((u) => ({
    userId:      u.userId      ?? "",
    userUUID:    u.userUUID    ?? "",
    userName:    u.userName    ?? "",
    location:    u.location    ?? "",
    nationality: u.nationality ?? "",
    religion:    u.religion    ?? "",
    joinedOn:    u.joinedOn    ?? "",
    lastupdate:  u.lastupdate  ?? "",
  }));

  // ── locationspec (food) ────────────────────────────────────────────────────
  const rawLocationSpec = Array.isArray(r.locationspec) ? r.locationspec : [];
  const locationspec = rawLocationSpec.map((item) => ({
    foodName:     item.foodName     ?? "",
    foodtype:     item.foodtype     ?? "",
    foodCategory: item.foodCategory ?? "",
    acceptancy:   item.acceptancy   ?? "",
  }));

  // ── eventspec (events held at this location) ───────────────────────────────
  const rawEventSpec = Array.isArray(r.eventspec) ? r.eventspec : [];
  const eventspec = rawEventSpec.map((item) => ({
    eventName:     item.eventName     ?? "",
    eventtype:     item.eventtype     ?? "",
    eventCategory: item.eventCategory ?? "",
    acceptancy:    item.acceptancy    ?? "",
  }));

  // ── Feeds ──────────────────────────────────────────────────────────────────
  const feeds = (r.feeds || []).map((feed) => {
    const locations = (feed.locations || []).map((loc) => ({
      locationId:   loc.locationId   ?? "",
      locationUUID: loc.locationUUID ?? "",
      profileImage: loc.profileImage ?? "",
      vulanteers: (loc.vulanteers || loc.volunteers || []).map((v) => ({
        userid:    v.userid    ?? "",
        useruuid:  v.useruuid  ?? "",
        username:  v.username  ?? "",
        userimage: v.userimage ?? "",
      })),
    }));

    const medias = (feed.medias || []).map((media) => ({
      mediaId:      media.mediaId      ?? "",
      mediauuid:    media.mediauuid    ?? "",
      descriptions: media.descriptions ?? "",
      uploadedBy:   media.uploadedBy   ?? "",
      mediasize:    media.mediasize    ?? "",
      mediatype:    media.mediatype    ?? "",
      imageurl:     media.imageurl     ?? "",
      nlikes:       media.nlikes   ?? 0,
      comments: (media.comments || []).map((c) => ({
        userId:    c.userId    ?? "",
        comment:   c.comment   ?? "",
        timestamp: c.timestamp ?? "",
        location:  c.location  ?? "",
      })),
      nshares:   media.nshares  ?? 0,
      nsuggests: media.nsuggests ?? 0,
    }));

    const comments = (feed.comments || []).map((c) => ({
      userId:    c.userId    ?? "",
      comment:   c.comment   ?? "",
      timestamp: c.timestamp ?? "",
      location:  c.location  ?? "",
    }));

    return {
      postId:       feed.postId       ?? "",
      userId:       feed.userId       ?? "",
      useruuid:     feed.useruuid     ?? "",
      userimage:    feed.userimage    ?? "",
      descriptions: feed.descriptions ?? "",
      timestamp:    feed.timestamp    ?? "",
      locations,
      nlikes:  feed.nlikes  ?? 0,
      medias,
      comments,
      nshares: feed.nshares ?? 0,
    };
  });

  return {
    ...LocationStructure,
    locationId:           r.locationId           ?? r.eventId           ?? "",
    locationuuid:         r.locationuuid         ?? r.eventuuid         ?? "",
    locationname:         r.locationname         ?? r.eventname         ?? "",
    country:              r.country              ?? "",
    religion:             r.religion             ?? "",
    locationtype:         r.locationtype         ?? r.eventtype         ?? "",
    locationcoverimage:   r.locationcoverimage   ?? r.eventcoverimage   ?? "",
    locationprofileimage: r.locationprofileimage ?? r.eventprofileimage ?? "",
    pincode:              r.pincode              ?? "",
    createdOn:            r.createdOn            ?? "",
    lastUpdate:           r.lastUpdate           ?? "",
    locationcycle:        r.locationcycle        ?? r.eventcycle        ?? "",
    advertises,
    users,
    locationspec,
    eventspec,
    feeds,
  };
}

// ─── Public fetchers (names match LocationGridLayout.js) ─────────────────────

// userid-locationid
export async function fetchLocationData(locationId, year) {
  const segment = encodeURIComponent(`${locationId}-${year}`);
  const url = `${BASE}/UserLocationPageMockData-${segment}.json`;
  const raw = await fetchJSON(url);
  return formatLocationData(raw);
}

// locationid-only
export async function fetchLocationIdOnly(locationId) {
  const segment = encodeURIComponent(locationId);
  const url = `${BASE}/UserLocationPageMockData-${segment}.json`;
  const raw = await fetchJSON(url);
  return formatLocationData(raw);
}

// countryid-locationid
export async function fetchCountryIdLocationId(countryId, locationId) {
  const segment = encodeURIComponent(`${countryId}-${locationId}`);
  const url = `${BASE}/UserLocationPageMockData-${segment}.json`;
  const raw = await fetchJSON(url);
  return formatLocationData(raw);
}

// locationname-year
export async function fetchLocationNameYear(locationName, year) {
  const segment = encodeURIComponent(`${locationName}-${year}`);
  const url = `${BASE}/UserLocationPageMockData-${segment}.json`;
  const raw = await fetchJSON(url);
  return formatLocationData(raw);
}

// userid-locationname-year
export async function fetchUserIdLocationNameYear(userId, locationName, year) {
  const segment = encodeURIComponent(`${userId}-${locationName}-${year}`);
  const url = `${BASE}/UserLocationPageMockData-${segment}.json`;
  const raw = await fetchJSON(url);
  return formatLocationData(raw);
}

// userid-countryid-locationid
export async function fetchUserIdCountryIdLocationId(userId, countryId, locationId) {
  const segment = encodeURIComponent(`${userId}-${countryId}-${locationId}`);
  const url = `${BASE}/UserLocationPageMockData-${segment}.json`;
  const raw = await fetchJSON(url);
  return formatLocationData(raw);
}

// ─── Default export (convenience object) ─────────────────────────────────────
export default {
  fetchLocationData,
  fetchLocationIdOnly,
  fetchCountryIdLocationId,
  fetchLocationNameYear,
  fetchUserIdLocationNameYear,
  fetchUserIdCountryIdLocationId,
  extractMediaByType,
  formatLocationData,
};
